import React, { useState, useEffect } from "react";
import globals from '../../../Globals';
import { Bar, Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const API = globals.API;

function DashboardOverview() {
  const [eventsCount, setEventsCount] = useState(0);
  const [usersCount, setUsersCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [eventsByCategory, setEventsByCategory] = useState({});
  const [usersByRole, setUsersByRole] = useState({});
  const [eventsPerMonth, setEventsPerMonth] = useState([]);

  useEffect(() => {
    async function fetchDashboardData() {
      try {
        // --- Fetch Events ---
        const response = await fetch(`${API}/events/`);
        const json = await response.json();
        const events = json.data || [];

        // Count total events
        setEventsCount(events.length);

        // Group events by category
        const categoryCounts = events.reduce((acc, ev) => {
          const cat = ev.category || 'Other';
          acc[cat] = (acc[cat] || 0) + 1;
          return acc;
        }, {});
        setEventsByCategory(categoryCounts);

        // 🗓️ Group events by month (using createdAt)
        const monthlyCounts = Array(12).fill(0);
        events.forEach((event) => {
          if (event.createdAt) {
            const monthIndex = new Date(event.createdAt).getMonth(); // 0-11
            monthlyCounts[monthIndex]++;
          }
        });
        setEventsPerMonth(monthlyCounts);

        // --- Fetch Users ---
        const token = localStorage.getItem('authToken');
         if (!token) {
    console.error('No auth token found');
    setLoading(false);
    return;
  }
        const userRes = await fetch(`${API}/users/`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
        const userJson = await userRes.json();
        const users = userJson.users || userJson.data || [];
        setUsersCount(users.length);

        const roleCounts = users.reduce((acc, user) => {
          const role = user.role || 'other';
          acc[role] = (acc[role] || 0) + 1;
          return acc;
        }, {});
        setUsersByRole(roleCounts);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchDashboardData();
  }, []);

  // --- Chart Data ---
  const eventsChartData = {
    labels: Object.keys(eventsByCategory),
    datasets: [
      {
        label: 'Événements',
        data: Object.values(eventsByCategory),
        backgroundColor: '#7551ff',
      },
    ],
  };

  const usersChartData = {
    labels: Object.keys(usersByRole),
    datasets: [
      {
        label: 'Utilisateurs',
        data: Object.values(usersByRole),
        backgroundColor: ['#4318ff', '#00c49f'],
      },
    ],
  };

  const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
  const eventsPerMonthData = {
    labels: months,
    datasets: [
      {
        label: 'Événements créés par mois',
        data: eventsPerMonth,
        borderColor: '#7551ff',
        backgroundColor: 'rgba(117,81,255,0.15)',
        tension: 0.3,
        fill: true,
        pointRadius: 5,
      },
    ],
  };

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>Overview</h2>

      {/* --- Cards (unchanged) --- */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
        }}
      >
        <div style={cardStyle}>
          <h3>Événements</h3>
          <p style={valueStyle}>{loading ? '...' : eventsCount}</p>
        </div>
        <div style={cardStyle}>
          <h3>Utilisateurs</h3>
          <p style={{ ...valueStyle, color: '#4318ff' }}>{loading ? '...' : usersCount}</p>
        </div>
      </div>

      {/* --- Charts Section --- */}
      <div
        style={{
          marginTop: '40px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '20px',
        }}
      >
        {/* Events by category */}
        <ChartCard title="Événements par Catégorie">
          <Bar data={eventsChartData} options={barOptions} />
        </ChartCard>

        {/* Users by role */}
        <ChartCard title="Utilisateurs par Rôle">
          <Pie data={usersChartData} options={pieOptions} />
        </ChartCard>
      </div>

      {/* --- Events per month line chart --- */}
      <div
  style={{
    marginTop: '30px',
    background: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    maxWidth: '800px',
    marginInline: 'auto',
  }}
>
  <h3 style={{ marginBottom: '10px', textAlign: 'center' }}>
    Évolution des Événements par Mois
  </h3>

  <div
    style={{
      height: '220px', // ✅ smaller height
      width: '100%',
    }}
  >
    <Line
      data={eventsPerMonthData}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { mode: 'index', intersect: false },
        },
        elements: {
          line: { tension: 0.4, borderWidth: 3 },
          point: { radius: 4, hoverRadius: 6 },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: 12 } },
          },
          y: {
            grid: { color: '#f2f2f2' },
            ticks: { stepSize: 1, precision: 0, font: { size: 12 } },
          },
        },
      }}
    />
  </div>
</div>
    </div>
  );
}

// --- Helper components & styles ---
const cardStyle = {
  background: 'white',
  padding: '20px',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};
const valueStyle = {
  fontSize: '32px',
  fontWeight: 'bold',
  color: '#7551ff',
};

const ChartCard = ({ title, children }) => (
  <div
    style={{
      background: 'white',
      padding: '20px',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
      minHeight: '320px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    }}
  >
    <h3 style={{ marginBottom: '10px' }}>{title}</h3>
    <div style={{ flexGrow: 1, height: '250px' }}>{children}</div>
  </div>
);

const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
};

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: { font: { size: 12 } },
    },
  },
};

const lineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { display: false } },
    y: { grid: { color: '#eee' }, beginAtZero: true },
  },
};
{/*
function DashboardEvents() {
  const [events, setEvents] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('Toutes les catégories');
  const [destinationFilter, setDestinationFilter] = useState('Toutes les destinations');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchEvents() {
      try {
        const response = await fetch('http://localhost:5000/events/'); // Replace with your actual API endpoint URL
        if (!response.ok) throw new Error('Network response was not ok');
        const result = await response.json();

        const mappedEvents = result.data.map(evt => ({
          id: evt._id,
          title: evt.title.replace(/\"/g, ''), // Clean extra quotes if any
          location: evt.destination,
          description: evt.description,
          included: evt.includedServices.join(', '),
          date: new Date(evt.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }),
          status: evt.isActive ? 'ACTIVE' : 'INACTIVE',
          category: evt.category.charAt(0).toUpperCase() + evt.category.slice(1),
          image: evt.image
        }));

        setEvents(mappedEvents);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    }
    fetchEvents();
  }, []);

 const handleDelete = async (id) => {
  if (window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) {
    try {
      const response = await fetch(`http://localhost:5000/events/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete event');
      // Remove from local state if API call succeeds
      setEvents(events.filter(event => event.id !== id));
    } catch (err) {
      alert(`Erreur lors de la suppression: ${err.message}`);
    }
  }
};
  const handleEdit = (id) => {
    alert(`Modifier l'événement #${id}`);
    // Implement edit functionality here (e.g., navigate or open modal)
  };

  const clearFilters = () => {
    setCategoryFilter('Toutes les catégories');
    setDestinationFilter('Toutes les destinations');
  };

  const filteredEvents = events.filter(event => {
    return (categoryFilter === 'Toutes les catégories' || event.category === categoryFilter) &&
           (destinationFilter === 'Toutes les destinations' || event.location === destinationFilter);
  });

  if (loading) return <p>Chargement des événements...</p>;
  if (error) return <p>Erreur: {error}</p>;

  return (
    <>
      <style>{`
      .events-container {
          background: #f5f7fa;
          min-height: 100vh;
          padding: 30px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .events-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 40px;
        }

        .events-title {
          font-size: 36px;
          font-weight: 700;
          color: #1a202c;
          margin: 0;
        }

        .create-btn {
          background: #4F46E5;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background 0.2s;
        }

        .create-btn:hover {
          background: #4338CA;
        }

        .section-header {
          margin-bottom: 20px;
        }

        .section-title {
          font-size: 24px;
          font-weight: 600;
          color: #1a202c;
          margin: 0 0 8px 0;
        }

        .event-count {
          color: #64748b;
          font-size: 14px;
        }

        .filters-container {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-bottom: 30px;
          flex-wrap: wrap;
        }

        .filter-label {
          font-weight: 600;
          color: #475569;
          font-size: 14px;
        }

        .filter-select {
          padding: 10px 16px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: white;
          font-size: 14px;
          color: #1a202c;
          cursor: pointer;
          min-width: 200px;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23475569' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          padding-right: 36px;
        }

        .clear-filters {
          color: #4F46E5;
          font-weight: 600;
          cursor: pointer;
          font-size: 14px;
          margin-left: auto;
        }

        .clear-filters:hover {
          text-decoration: underline;
        }

        .events-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
          gap: 24px;
        }

        .event-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          transition: box-shadow 0.2s;
        }

        .event-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .event-image-container {
          position: relative;
          height: 200px;
          overflow: hidden;
        }

        .event-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .event-status {
          position: absolute;
          top: 12px;
          left: 12px;
          background: #10b981;
          color: white;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
        }

        .event-category {
          position: absolute;
          top: 12px;
          right: 12px;
          background: rgba(255, 255, 255, 0.95);
          color: #4F46E5;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
        }

        .event-content {
          padding: 20px;
        }

        .event-title {
          font-size: 20px;
          font-weight: 700;
          color: #1a202c;
          margin: 0 0 8px 0;
        }

        .event-location {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #4F46E5;
          font-size: 14px;
          margin-bottom: 12px;
        }

        .event-description {
          color: #64748b;
          font-size: 14px;
          line-height: 1.6;
          margin-bottom: 12px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .event-included {
          color: #1a202c;
          font-size: 13px;
          margin-bottom: 16px;
          font-weight: 500;
        }

        .event-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 16px;
          border-top: 1px solid #e2e8f0;
        }

        .event-date {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #64748b;
          font-size: 14px;
        }

        .event-actions {
          display: flex;
          gap: 8px;
        }

        .btn-edit, .btn-delete {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .btn-edit {
          background: white;
          color: #4F46E5;
          border: 1px solid #4F46E5;
        }

        .btn-edit:hover {
          background: #EEF2FF;
        }

        .btn-delete {
          background: white;
          color: #ef4444;
          border: 1px solid #ef4444;
        }

        .btn-delete:hover {
          background: #FEF2F2;
        }

        @media (max-width: 768px) {
          .events-container {
            padding: 20px;
          }

          .events-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 15px;
          }

          .events-grid {
            grid-template-columns: 1fr;
          }

          .filters-container {
            flex-direction: column;
            align-items: stretch;
          }

          .filter-select {
            width: 100%;
          }

          .clear-filters {
            margin-left: 0;
          }
        }
      `}</style>

      <div className="events-container">
        <div className="events-header">
          <h1 className="events-title">Événements</h1>
          <button className="create-btn">
            + Créer un nouvel événement
          </button>
        </div>

        <div className="section-header">
          <h2 className="section-title">Mes Événements</h2>
          <p className="event-count">{filteredEvents.length} sur {events.length} événements affichés</p>
        </div>

        <div className="filters-container">
          <span className="filter-label">Filtrer par:</span>
          <select 
            className="filter-select" 
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option>Toutes les catégories</option>
            <option>Omra</option>
            <option>Voyage</option>
          </select>

          <select 
            className="filter-select"
            value={destinationFilter}
            onChange={(e) => setDestinationFilter(e.target.value)}
          >
            <option>Toutes les destinations</option>
            <option>Zanzibar</option>
            <option>Everest</option>
          </select>

          <span className="clear-filters" onClick={clearFilters}>
            Effacer les filtres
          </span>
        </div>

        <div className="events-grid">
          {filteredEvents.map(event => (
            <div key={event.id} className="event-card">
              <div className="event-image-container">
                <img src={event.image} alt={event.title} className="event-image" />
                <span className="event-status">{event.status}</span>
                <span className="event-category">{event.category}</span>
              </div>

              <div className="event-content">
                <h3 className="event-title">{event.title}</h3>
                
                <div className="event-location">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  {event.location}
                </div>

                <p className="event-description">{event.description}</p>

                {event.included && (
                  <p className="event-included">Inclus: {event.included}</p>
                )}

                <div className="event-footer">
                  <div className="event-date">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM9 14H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2zm-8 4H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2z"/>
                    </svg>
                    {event.date}
                  </div>

                  <div className="event-actions">
                    <button className="btn-edit" onClick={() => handleEdit(event.id)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                      </svg>
                      Modifier
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(event.id)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                      </svg>
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}  */}







export default DashboardOverview;